import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

const emptyForm = {
  name: "",
  code: "",
  sizeGroupId: "",
  sortOrder: 0,
  isActive: true,
};

export default function SizeModal({
  open,
  onClose,
  onSave,
  initialData = null,
  sizeGroups = [],
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            name: initialData.name || "",
            code: initialData.code || "",
            sizeGroupId: initialData.sizeGroupId || "",
            sortOrder: initialData.sortOrder ?? 0,
            isActive: initialData.isActive ?? true,
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();

    onSave({
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      sizeGroupId: form.sizeGroupId,
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
    });
  };

  const activeSizeGroups = sizeGroups.filter(
    (sizeGroup) => sizeGroup.isActive,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {initialData ? "Edit Size" : "Add Size"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Add sizes to a reusable size group.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 p-8">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Size Name *
              </label>

              <input
                type="text"
                placeholder="Medium or 32"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Size Code *
              </label>

              <input
                type="text"
                placeholder="M or 32"
                value={form.code}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Size Group *
              </label>

              <select
                value={form.sizeGroupId}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    sizeGroupId: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                required
              >
                <option value="">Select Size Group</option>

                {activeSizeGroups.map((sizeGroup) => (
                  <option key={sizeGroup.id} value={sizeGroup.id}>
                    {sizeGroup.name} ({sizeGroup.code})
                  </option>
                ))}
              </select>

              {activeSizeGroups.length === 0 && (
                <p className="mt-2 text-sm text-red-600">
                  Create a Size Group first.
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Display Order
              </label>

              <input
                type="number"
                min="0"
                value={form.sortOrder}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    sortOrder: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Status
              </label>

              <select
                value={form.isActive ? "active" : "inactive"}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isActive: event.target.value === "active",
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl border border-gray-200 px-6 text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={activeSizeGroups.length === 0}
              className="h-11 rounded-xl bg-primary-800 px-6 text-sm font-medium text-white hover:bg-primary-900 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {initialData ? "Save Changes" : "Create Size"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}